import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react"
import { throttle } from "lodash"
import { ThemeContext } from "styled-components"
import PropTypes from "prop-types"
import { notGatsby, graphql } from "gatsby"
import styled from "styled-components"
import { shadow, color, compose, layout } from "styled-system"
import css from "../../../lib/styled-system/css"
import { hexToRGBA } from "../../../lib/color-helpers"
import useBreakpoint from "../../../hooks/use-breakpoint"

import ThemeSwitcher from "../../utils/ThemeSwitcher"
import Reveal from "../../utils/Reveal"
import HamburgerIcon from "../../icons/Hamburger"

import StandardPadding from "../../atoms/StandardPadding"
import Flex from "../../atoms/Flex"
import Box from "../../atoms/Box"
import Button from "../../atoms/Button"
import ButtonElement from "../../atoms/ButtonElement"

import MobileMenu from "../../molecules/MobileMenu"
import AnnouncementBar from "../AnnouncementBar"

import * as Utils from "./utils"

export const StyledHeader = styled("header")(
  // Static styles
  {
    position: "relative",
    transition: `background-color cubic-bezier(.17,.67,.83,.67) .5s`,
  },
  // Static styles that are defined by the theme
  css({ zIndex: "header" }),
  // Dynamic styles that can be changed by props
  compose(color, shadow, layout)
)

const HideNavBox = styled(Box)`
  transition: top cubic-bezier(0.17, 0.67, 0.62, 0.88) 0.2s;
  transition: background-color cubic-bezier(0.17, 0.67, 0.83, 0.67) 0.5s;
`

const HeaderDefault = props => {
  const [menu, setMenu] = useState(false)
  const {
    logoObj,
    themeObj,
    hideNavProps,
    logoShift,
    styledHeaderProps,
    firstButtonProps,
    linksWrapperProps,
  } = Utils.useMemoizedProps(props)

  const socialsLocation = props.socialLinksLocation || "rightOfLinks"
  const socialsMobileLocation = props.mobileIconLocation || "belowLinks"
  const isStandardLayout =
    props?.layout === "nav-standard" ||
    props?.layout === "nav-full-width" ||
    !props?.layout
  const isCenteredLayout =
    props?.layout === "nav-centered" || props?.layout === "nav-stacked"
  const showHamburger =
    !!props.button || !!props.button2 || !!props.links?.length
  const isMobileBp = useBreakpoint("lg")

  const slug = props.site?.pages?.[0]?.slug

  const ConditionalPadding = ({ condition, wrapper, children }) =>
    condition ? wrapper(children) : children

  // *SCROLL NAV APPEAR/DISAPPEAR LOGIC*
  const top = 0
  const [showNavTop, setShowNavTop] = useState(top)
  const scrollPos = useRef(0)
  const navRef = useRef(null)
  const [navHeight, setNavHeight] = useState(0)

  // Transparent nav bg logic - set to first section bg when scrolling
  const [bgTransparency, setBgTransparency] = useState(undefined)
  const currentTheme = useContext(ThemeContext)
  const bgOpacityPercent = props.bgOpacityNav * 100 || 100
  let bgColorWithOpacity
  let conditionalProps = {}

  if (props.isTransparentNav) {
    const firstSectionTheme =
      props.firstSectionStyling?.theme === "default"
        ? currentTheme?.backgroundTheme === "mixed"
          ? "light"
          : currentTheme?.backgroundTheme
        : props.firstSectionStyling
        ? props.firstSectionStyling?.theme
        : "light"

    const bgColor =
      props.firstSectionStyling?.bg && firstSectionTheme
        ? currentTheme?.themes[firstSectionTheme].colors?.background[
            props.firstSectionStyling?.bg
          ]
        : "#ffffff"
    bgColorWithOpacity = hexToRGBA(bgColor, bgOpacityPercent)

    if (bgTransparency) {
      conditionalProps.bg = bgTransparency
    }
  }

  const navHeightRef = useRef(navHeight)
  const bgColorWithOpacityRef = useRef(bgColorWithOpacity)

  useEffect(() => {
    navHeightRef.current = navHeight
  }, [navHeight])

  useEffect(() => {
    bgColorWithOpacityRef.current = bgColorWithOpacity
  }, [bgColorWithOpacity])

  // Wrap the scroll handler with usecallback and throttle
  const scrollUpHandler = useCallback(
    throttle(() => {
      let scrollTop = window.pageYOffset || document.documentElement.scrollTop
      if (props?.navBarDisplay === "appear") {
        if (
          scrollTop > scrollPos.current &&
          window.scrollY > navHeightRef.current / 2
        ) {
          setShowNavTop(-navHeightRef.current)
          setBgTransparency(undefined)
        } else if (scrollTop < 40) {
          setShowNavTop(top)
          setBgTransparency(undefined)
        } else {
          setShowNavTop(top)
          setBgTransparency(bgColorWithOpacityRef.current)
        }
      } else if (props?.navBarDisplay === "scroll") {
        if (scrollTop > 40) {
          setBgTransparency(bgColorWithOpacityRef.current)
        } else {
          setBgTransparency(undefined)
        }
      }
      scrollPos.current = scrollTop
    }, 200),
    [props.navBarDisplay, top]
  )

  useEffect(() => {
    if (navRef.current) {
      if (notGatsby) {
        setNavHeight(navRef.current.clientHeight)
      } else {
        setNavHeight(navRef.current.getBoundingClientRect().height)
      }
    }
  }, [props?.layout])

  // Scroll handler effects
  useEffect(() => {
    if (
      props?.navBarDisplay === "appear" ||
      props?.navBarDisplay === "scroll"
    ) {
      window.addEventListener("scroll", scrollUpHandler)
    }
    return () => {
      window.removeEventListener("scroll", scrollUpHandler)
      // Cancel any pending throttled calls
      scrollUpHandler.cancel()
    }
  }, [scrollUpHandler, props?.navBarDisplay])

  return (
    <ThemeSwitcher theme={themeObj.theme}>
      {props?.navBarDisplay === "lock" && props?.announcementBar && (
        <AnnouncementBar
          {...props?.announcementBar}
          slug={slug}
          isHomepage={props?.isHomepage}
          menuOpen={menu}
        />
      )}
      <Box
        position="relative"
        height={
          props?.navBarDisplay === "appear"
            ? { _: "60px", lg: `${navHeight}px` }
            : 0
        }
        width="100%"
      />
      <HideNavBox
        ref={navRef}
        zIndex="header"
        top={
          props?.navBarDisplay === "scroll"
            ? 0
            : props?.navBarDisplay === "appear"
            ? showNavTop
            : "inherit"
        }
        width="100%"
        {...hideNavProps}
        {...conditionalProps}
      >
        {/*this box is to keep the same header background while the header finish loading*/}
        <Reveal>
          <StyledHeader
            boxShadow={props?.shadow || "md"}
            display={props?.headerVisibility === "hidden" ? "none" : "block"}
            height={props?.layout === "nav-stacked" ? { _: 5, lg: 8 } : 5}
            {...styledHeaderProps}
            {...conditionalProps}
          >
            <ConditionalPadding
              condition={props?.layout === "nav-standard"}
              wrapper={children => (
                <StandardPadding py={0} height="100%">
                  {children}
                </StandardPadding>
              )}
            >
              {isStandardLayout && !isCenteredLayout && (
                <Flex
                  alignItems="center"
                  flexDirection={
                    props?.logoPosition === "right" ? "row-reverse" : "row"
                  }
                  justifyContent={
                    props.navAlignment === "left"
                      ? { _: "space-between", lg: "flex-start" }
                      : {
                          _: "space-between",
                          lg: "center",
                          xl: "space-between",
                        }
                  }
                  height="100%"
                  px={props?.layout === "nav-full-width" && 5}
                >
                  <>
                    {Utils.renderLogo(props, logoObj, logoShift)}
                    <Box
                      flex="1 1 100%"
                      as="nav"
                      alignItems="center"
                      display={{ _: "none", lg: "flex" }}
                      {...linksWrapperProps}
                    >
                      <Flex
                        alignItems="center"
                        display={{ lg: "flex" }}
                        style={
                          socialsLocation === "betweenLinksAndButtons"
                            ? {
                                width: "100%",
                                justifyContent: "center",
                              }
                            : {}
                        }
                      >
                        {socialsLocation === "leftOfLinks" &&
                          Utils.renderSocialLinks(props)}
                        {Utils.renderHeaderLinks(props)}
                        {socialsLocation === "rightOfLinks" &&
                          Utils.renderSocialLinks(props)}
                      </Flex>
                      <Flex alignItems="center">
                        {socialsLocation === "betweenLinksAndButtons" &&
                          Utils.renderSocialLinks(props)}
                        {props.button && (
                          <Button
                            {...firstButtonProps}
                            hideArrow
                            aria-label="CTA Button 1"
                          />
                        )}
                        {props.button2 && (
                          <Button
                            {...props.button2}
                            ml={props?.logoPosition === "left" ? 4 : 0}
                            mr={props?.logoPosition === "right" ? 4 : 0}
                            hideArrow
                            aria-label="CTA Button 2"
                          />
                        )}
                      </Flex>
                      {socialsLocation === "rightOfLinksAndButtons" &&
                        Utils.renderSocialLinks(props)}
                    </Box>
                    <Flex alignItems="center">
                      <Box display={{ _: "block", lg: "none" }}>
                        {(socialsMobileLocation === "navigationBar" ||
                          (socialsMobileLocation === "belowLinks" &&
                            !showHamburger)) &&
                          Utils.renderSocialLinks(props)}
                      </Box>
                      {showHamburger && (
                        <ButtonElement
                          aria-label="Open mobile menu"
                          onClick={() => setMenu(true)}
                          ml={
                            props?.logoPosition === "left" || isCenteredLayout
                              ? 5
                              : null
                          }
                          mr={props?.logoPosition === "right" ? 5 : null}
                          width={2}
                          height={2}
                          display={{ _: "block", lg: "none" }}
                        >
                          <HamburgerIcon color="text" />
                        </ButtonElement>
                      )}
                    </Flex>
                  </>
                </Flex>
              )}
              {isCenteredLayout && !isStandardLayout && (
                <>
                  {Utils.renderCenteredLayout(
                    props,
                    logoObj,
                    logoShift,
                    themeObj,
                    setMenu,
                    isMobileBp
                  )}
                </>
              )}
            </ConditionalPadding>
          </StyledHeader>
        </Reveal>
      </HideNavBox>
      <MobileMenu
        isOpen={menu}
        onDismiss={() => setMenu(false)}
        links={props?.links}
        logo={Utils.renderLogo(props, logoObj, "left center")}
        button={props?.button}
        button2={props?.button2}
        socialLinks={props?.socialLinks}
        socialLinkStyles={props?.socialLinkStyles}
        bg={`background.${props?.bg || 0}`}
        socialsMobileLocation={socialsMobileLocation}
        homepagePageId={props?.homepagePageId}
        linkAlignmentOnMobile={props?.linkAlignmentOnMobile}
      />
    </ThemeSwitcher>
  )
}

HeaderDefault.strapiProps = {
  logo: PropTypes.shape({
    url: PropTypes.string,
    alternativeText: PropTypes.string,
  }),
  links: PropTypes.array,
  button: PropTypes.shape(Button.strapiProps),
  shadow: PropTypes.string.isRequired,
  theme: PropTypes.oneOf(["default", "light", "dark", "highlight"]).isRequired,
  bg: PropTypes.number,
  headerVisibility: PropTypes.oneOf(["visible", "hidden"]),
  headerWidth: PropTypes.oneOf(["default", "fullWidth"]),
  navAlignment: PropTypes.oneOf(["left", "right"]),
  linkAlignmentOnMobile: PropTypes.oneOf(["left", "center"]),
  logoPosition: PropTypes.oneOf(["left", "right"]),
  dropDownOpenOn: PropTypes.oneOf(["click", "hover"]),
  dropdownPlacement: PropTypes.oneOf(["left", "center"]),
  navBarDisplay: PropTypes.oneOf(["scroll", "lock", "appear"]),
}

HeaderDefault.propTypes = {
  ...HeaderDefault.strapiProps,
  siteTitle: PropTypes.string.isRequired,
}

export default HeaderDefault

export const query = graphql`
  fragment HeaderDefault on STRAPI__COMPONENT_HEADERS_DEFAULT {
    strapi_id
    __typename
    headerVisibility
    layout
    linkBarTheme
    linkBarBg
    headerWidth
    theme
    bg
    shadow
    navAlignment
    linkAlignmentOnMobile
    logoPosition
    dropDownOpenOn
    dropdownPlacement
    socialLinks {
      ...SocialLink
    }
    socialLinkStyles {
      ...SocialLinkStyles
    }
    socialLinksLocation
    socialLinkSize
    socialLinkSpacing
    mobileIconLocation
    logoSize {
      sizeSelections
      customSize
      customSizeMobile
    }
    navBarDisplay
    announcementBar {
      bg
      homepageOnly
      lockedToTop
      shadow
      showAnnouncementBar
      text
      theme
      link {
        ...Link
      }
    }
    card {
      bg
      borderRadius
      boxShadow
      theme
    }
    logo {
      url
      alternativeText
      localFile {
        childImageSharp {
          gatsbyImageData(
            formats: [AUTO, WEBP, AVIF]
            quality: 100
            width: 550
            placeholder: NONE
            layout: CONSTRAINED
          )
        }
      }
    }
    logoMedium {
      url
      alternativeText
      localFile {
        childImageSharp {
          gatsbyImageData(
            formats: [AUTO, WEBP, AVIF]
            quality: 100
            width: 550
            placeholder: NONE
            layout: CONSTRAINED
          )
        }
      }
    }
    logoDark {
      url
      alternativeText
      localFile {
        childImageSharp {
          gatsbyImageData(
            formats: [AUTO, WEBP, AVIF]
            quality: 100
            width: 550
            placeholder: NONE
            layout: CONSTRAINED
          )
        }
      }
    }
    homepagePageId
    links {
      __typename
      ... on STRAPI__COMPONENT_UTILS_LINK {
        ...Link
      }
      ... on STRAPI__COMPONENT_UTILS_DROPDOWN {
        name
        links {
          ...Link
        }
      }
      ... on STRAPI__COMPONENT_UTILS_MEGA_MENU {
        ...NavMegaMenu
      }
    }
    button {
      ...Button
    }
    button2 {
      ...Button
    }
    buttonAlignment
  }
`
